import React, {
    useEffect, useMemo, useRef, useState,
} from 'react';
import { useInView } from 'framer-motion';
import { useCookies } from 'react-cookie';
import { useTranslation } from 'react-i18next';

import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { fas, faBox } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { resolveTagIcon } from '@r6digital/rapidstor-sdk/domain';
import type { UnitType, UnitTypeCollection } from '@r6digital/rapidstor-sdk/domain';
import type RawDescriptor from '@r6digital/rapidstor-types/react/entities/Descriptor';
import type Location from '@r6digital/rapidstor-types/react/entities/Location';

import store from '@/store';
import { setDeal, setSizesViewed } from '@/store/bookingSlice';
import Button from '@/standard-unit-listing/components/elements/button';
import classes from '@/standard-unit-listing/components/unit-type/styles.module.scss';

import { rawDealFor } from '../../sdk/interop';
import { useSite } from '../../sdk/useSite';
import UnitTypeMeta from '../shared/UnitTypeMeta';
import CTAGroup from './CTAGroup';
import DealPriceCards from './DealPriceCards';
import UnitTypeImageGallery from './ImageGallery';

const solidIconsByName: Record<string, IconDefinition> = Object.fromEntries(
    Object.values(fas).map((icon) => [icon.iconName, icon]),
);

export interface UnitTypeCardProps {
    /**
     * The variants this card presents. A flat listing passes one; a multilevel listing passes one per
     * floor category.
     */
    variants: UnitTypeCollection;
    location: Location;
    /**
     * The friendly descriptors the listing was built from — needed only to map back to raw entities
     * at the Redux/details-modal boundary. See `sdk/interop.ts`.
     */
    descriptors: RawDescriptor[];
    onBookOnline?: (unitType: UnitType) => void;
    onSendEnquiry?: (unitType: UnitType) => void;
    onShowPrices?: (unitType: UnitType) => void;
}

/**
 * A single unit type card: gallery, title, variant selector, meta strip, description, tags and the
 * pricing column.
 *
 * Every value rendered comes off the SDK's `UnitType` — including its descriptor's merchandising,
 * reached through the back-reference. What remains app-side is the Redux writes, the tracking, and
 * the cookie gate on hidden prices.
 */
export default function UnitTypeCard({
    variants,
    location,
    descriptors,
    onBookOnline,
    onSendEnquiry,
    onShowPrices,
}: UnitTypeCardProps) {
    const { t } = useTranslation();
    const site = useSite(location);

    const [activeIndex, setActiveIndex] = useState(0);
    const active = variants.at(activeIndex);

    // Selected deal for the active variant (null = standard price). Defaults to the promoted deal,
    // matching the reservation flow.
    const [selectedDealId, setSelectedDealId] = useState<string | null>(null);
    const [cookies] = useCookies(['contactData']);
    const ref = useRef<HTMLDivElement>(null);
    const isInView = useInView(ref, { once: true });

    const sizeBadgeLabel = useMemo<string | null>(() => {
        if (!active) return null;
        switch (active.sizeCategory) {
            case 'small':
                return t('standardUnitListing:unitType:smallUnit', 'Small Unit');
            case 'medium':
                return t('standardUnitListing:unitType:mediumUnit', 'Medium Unit');
            case 'large':
                return t('standardUnitListing:unitType:largeUnit', 'Large Unit');
            default:
                return null;
        }
    }, [active, t]);

    useEffect(() => {
        if (isInView && active) {
            store.dispatch(
                setSizesViewed(active.formatDimensions({ spaceAroundSeparator: false })),
            );
        }
    }, [isInView, active]);

    // Deals differ per variant, so reset to the promoted one whenever the variant changes.
    useEffect(() => {
        setSelectedDealId(active?.deals.promoted()?.id ?? null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [active?.id]);

    if (!active) return null;

    const { descriptor } = active;
    const enabledTags = descriptor?.enabledTags({ limit: 4 }) ?? [];

    /**
     * Pushes the chosen deal into the reservation store so the booking/enquiry flows use it, then
     * delegates to the parent handler.
     */
    const withSelectedDeal = (handler?: (unitType: UnitType) => void) => (unitType: UnitType) => {
        // The store is typed against the raw Deal — an app-interop boundary, not listing logic.
        store.dispatch(
            setDeal({
                deal: selectedDealId,
                dealObject: rawDealFor(unitType, selectedDealId, descriptors),
            }),
        );
        handler?.(unitType);
    };

    return (
        <section
            className={classes.card}
            role="complementary"
            aria-label="UnitType pricing and actions"
            ref={ref}
        >
            <UnitTypeImageGallery unitType={active} location={location} />
            <div className={classes.unitTypeDetails}>
                <div className={classes.titleRow}>
                    <span className={classes.title}>{active.title()}</span>
                    {sizeBadgeLabel ? (
                        <span className={classes.sizeBadge}>{sizeBadgeLabel}</span>
                    ) : null}
                </div>

                {variants.length > 1 ? (
                    <fieldset className={classes.variantSelector}>
                        <div className={classes.variantOptions}>
                            {variants.map((variant, idx) => (
                                <label key={variant.id} className={classes.variantOption}>
                                    <input
                                        type="radio"
                                        name={`variant-${variants.first()?.id}`}
                                        value={idx}
                                        checked={activeIndex === idx}
                                        onChange={() => setActiveIndex(idx)}
                                    />
                                    <span>
                                        {t('standardUnitListing:unitType:level', 'Level')}
                                        :
                                        {' '}
                                        {variant.floorLabel}
                                    </span>
                                </label>
                            ))}
                        </div>
                    </fieldset>
                ) : null}

                <UnitTypeMeta unitType={active} location={location} classes={classes} />

                <div className={classes.description}>
                    <span className={classes.header}>
                        {t('standardUnitListing:unitType:whatCanItFit')}
                    </span>
                    <span className={classes.body}>{descriptor?.description}</span>
                </div>

                {enabledTags.length > 0 ? (
                    <div className={classes.tags}>
                        {enabledTags.map((tag, idx) => (
                            <span key={`${tag.icon}_${idx}`} className={classes.tag}>
                                <span className={classes.iconWrap}>
                                    <FontAwesomeIcon
                                        width={20}
                                        icon={solidIconsByName[resolveTagIcon(tag.icon)] || faBox}
                                        className={classes.icon}
                                    />
                                </span>
                                <span className={classes.content}>{tag.content}</span>
                            </span>
                        ))}
                    </div>
                ) : null}
            </div>
            <div className={classes.pricingContainer}>
                {site.settings.display().hidePrices() && !cookies.contactData ? (
                    <Button
                        type="primary"
                        size="medium"
                        style={{ minWidth: '180px' }}
                        onclick={() => onShowPrices?.(active)}
                    >
                        {site.settings.labels().showPricesButton()}
                    </Button>
                ) : (
                    <>
                        <DealPriceCards
                            unitType={active}
                            location={location}
                            selectedDealId={selectedDealId}
                            onSelectDeal={setSelectedDealId}
                        />
                        <CTAGroup
                            unitType={active}
                            location={location}
                            onBookOnline={withSelectedDeal(onBookOnline)}
                            onSendEnquiry={withSelectedDeal(onSendEnquiry)}
                        />
                    </>
                )}
            </div>
        </section>
    );
}
